import React from 'react';
import { sendMetric } from '@mentimeter/errors/sendMetric';
import { useTimeout } from '@mentimeter/react-hooks';
import {
  ClientErrorCodes,
  FORCE_DISCONNECTED_EVENT,
  GameStateEnums,
  type GameStates,
  type InitialGameStates,
  PresenterRoutes,
} from '@mentimeter/quiz-types';
import { getAdjustedFutureDate } from '../TimeSync';
import { useQuizResults } from '../quiz-result-provider';
import { useQuizConfig } from '../quiz-config.presenter';
import { useQuizStatus } from './use-quiz-status';
import { useEmit } from './use-emit';
import { useSocketIoConnection } from './connection-provider';
import { useSubscribe } from './use-subscribe';
import { useSubscribeRespondents } from './use-subscribe-respondents';
import { useSubscribePlayers } from './use-subscribe-players';

// Seems to be needing to match Quiz API.
export const GET_READY_COUNTDOWN_DELAY = 3000;
const EVERYONE_HAS_VOTED_DELAY = 3000;

const timeDiffFromNow = (t: number) => {
  return Math.abs(Date.now() - t);
};

export const useQuizGameState = () => {
  const config = useQuizConfig();
  const { emit } = useEmit(config);
  const { fetchResults } = useQuizResults();

  /**
   * One timer to make sure we can control it
   * Cleanup will run whenever unmounting to prevent un-cancelled timers
   */
  const timer = React.useRef<NodeJS.Timeout | null>(null);
  const waitFor = React.useCallback((duration: number) => {
    return new Promise<void>((resolve) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        resolve();
      }, duration);
    });
  }, []);

  // Cleanup timers when hook unmounts to prevent setting state if timers are active
  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const localState = React.useRef<'idle' | 'created' | 'started' | 'ended'>(
    'idle',
  );

  const { connection } = useSocketIoConnection();
  const { connected, connectedWithDelay, timeOffset } =
    useQuizStatus(connection);

  const { connectedPlayers, totalPlayersCount, resetPlayers } =
    useSubscribePlayers();
  const { respondents, resetRespondents } = useSubscribeRespondents();

  // These values are used in quiz api for logging purposes,
  // so it's ok if they are stale.
  const playerStatsForLogging = React.useRef({
    playerCount: totalPlayersCount,
    respondents: respondents.length,
  });
  playerStatsForLogging.current.playerCount = totalPlayersCount;
  playerStatsForLogging.current.respondents = respondents.length;

  const [gameState, setGameState] = React.useState<GameStates>();
  const reset = React.useCallback(() => {
    localState.current = 'idle';
    setGameState(undefined);
    resetRespondents();
    resetPlayers();
  }, [resetRespondents, resetPlayers]);

  const setClientError = React.useCallback((errorCode: ClientErrorCodes) => {
    setGameState({
      gameState: GameStateEnums.ERROR,
      gameStateData: {},
      errorCode,
    });
  }, []);

  // Force disconnected means another presenter has connected and we should
  // abandon the quiz in this device
  useSubscribe({
    event: FORCE_DISCONNECTED_EVENT,
    callback: React.useCallback(() => {
      localState.current = 'idle';
      setClientError(ClientErrorCodes.MULTIPLE_DEVICES);
      if (connection?.disconnect) {
        // We will not recover from this
        connection.disconnect();
      }
    }, [setClientError, connection]),
  });

  const createQuiz = React.useCallback(
    async (state: InitialGameStates) => {
      reset();
      try {
        const response = await emit(PresenterRoutes.CreateQuiz, {
          gameState: state,
        });
        localState.current = 'created';
        setGameState(response);
      } catch (error) {
        // NOOP as we get server errors in the response
      }
    },
    [emit, reset],
  );

  const _emitStartGetReady = React.useCallback(
    async (data: { getReadyForCountdown: number; playerCount: number }) => {
      const response = await emit(PresenterRoutes.StartQuiz, data);
      if (!response.errorCode) {
        localState.current = 'started';
      }
      setGameState(response);
      return response;
    },
    [emit],
  );

  const _emitStartCountdown = React.useCallback(
    async (data: { countdownToVote: number; playerCount: number }) => {
      const response = await emit(PresenterRoutes.StartCountdown, data);

      setGameState(response);
      return response;
    },
    [emit],
  );

  const endQuiz = React.useCallback(async () => {
    if (localState.current !== 'started') return;

    try {
      // Clear results cache and repopulate
      await fetchResults();
    } catch (error) {
      setClientError(ClientErrorCodes.QUESTION_SCORE_ERROR);
      return;
    }

    try {
      const response = await emit(
        PresenterRoutes.EndQuiz,
        playerStatsForLogging.current,
      );

      if (!response.errorCode) {
        localState.current = 'ended';
        sendMetric({
          name: 'mmjs.quiz_ending',
          value: 1,
          tags: [`zone:${window.location.host}`],
        });
      }

      setGameState(response);
    } catch (error) {
      // NOOP as we get server errors in the response
    }
  }, [emit, fetchResults, setClientError]);

  const changePlayerName = React.useCallback(
    async (data: { identifier: string; name: string }) => {
      try {
        await emit(PresenterRoutes.PlayerChangeName, data);
      } catch (error) {
        // No reason to disrupt the quiz with this error
      }
    },
    [emit],
  );

  const startQuiz = React.useCallback(
    async ({ countdownToVote }: { countdownToVote: number }) => {
      try {
        if (localState.current !== 'created') return;

        sendMetric({
          name: 'mmjs.quiz_starting',
          value: 1,
          tags: [`zone:${window.location.host}`],
        });

        // Initial get ready state
        await _emitStartGetReady({
          getReadyForCountdown: GET_READY_COUNTDOWN_DELAY / 1000,
          playerCount: playerStatsForLogging.current.playerCount,
        });

        // Wait for countdown to start
        await waitFor(GET_READY_COUNTDOWN_DELAY);

        // Start countdown to vote
        const {
          gameStateData: { endAt },
        } = await _emitStartCountdown({
          countdownToVote,
          playerCount: playerStatsForLogging.current.playerCount,
        });

        // Wait for quiz to end
        await waitFor(
          timeDiffFromNow(getAdjustedFutureDate(endAt, timeOffset ?? 0)),
        );

        // Quiz has ended
        await endQuiz();
      } catch (error) {
        setClientError(ClientErrorCodes.START_QUIZ_ERROR);
      }
    },
    [
      _emitStartCountdown,
      _emitStartGetReady,
      endQuiz,
      setClientError,
      timeOffset,
      waitFor,
    ],
  );

  // Everyone has voted
  const [everyoneHasVoted, setEveryoneHasVoted] = React.useState(false);
  React.useEffect(() => {
    if (respondents.length === 0 || totalPlayersCount !== respondents.length)
      return;
    // This value can only go from false to true, to not clear timer on change.
    setEveryoneHasVoted(true);
  }, [totalPlayersCount, respondents.length]);
  useTimeout(endQuiz, everyoneHasVoted ? EVERYONE_HAS_VOTED_DELAY : null);

  return {
    createQuiz,
    endQuiz,
    startQuiz,
    changePlayerName,
    gameState,
    connectedPlayers,
    respondents,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
    timeOffset,
  };
};
